<template>
	<div class="downstream-distribution-preview component-section" @click="handleClick">
		<div class="preview-header">
			<h3>Downstream Distribution Preview</h3>
			<p>Example data showing table structure and theming</p>
		</div>

		<DataTable
			:value="previewData"
			responsiveLayout="scroll"
			:row-hover="true"
			:paginator="false"
			class="data-table preview-table"
		>
			<ColumnGroup type="header">
				<Row>
					<Column header="" :colspan="1" style="background-color: white" />
					<Column
						header="Transport"
						:colspan="3"
						class="transport-group-header"
						style="
							background-color: var(--p-downstream-distribution-table-transport-group-background-color);
							color: var(--p-downstream-distribution-table-transport-group-text-color);
						"
					/>
					<Column
						header="Warehousing"
						:colspan="4"
						class="warehousing-group-header"
						style="
							background-color: var(--p-downstream-distribution-table-wharehousing-group-background-color);
							color: var(--p-downstream-distribution-table-wharehousing-group-text-color);
						"
					/>
					<Column header="" :colspan="1" style="background-color: white" />
				</Row>
				<Row>
					<Column
						header="Step"
						:colspan="1"
						class="step-field-header"
						style="
							background-color: var(--p-downstream-distribution-table-step-field-background-color);
							color: var(--p-downstream-distribution-table-step-field-text-color);
						"
					/>
					<Column
						header="Mode Of Transport"
						class="transport-group-subheader"
						style="
							background-color: var(--p-downstream-distribution-table-transport-group-background-color);
							color: var(--p-downstream-distribution-table-transport-group-text-color);
						"
					/>
					<Column
						header="Distance"
						class="transport-group-subheader"
						style="
							background-color: var(--p-downstream-distribution-table-transport-group-background-color);
							color: var(--p-downstream-distribution-table-transport-group-text-color);
						"
					/>
					<Column
						header="Unit"
						class="transport-group-subheader"
						style="
							background-color: var(--p-downstream-distribution-table-transport-group-background-color);
							color: var(--p-downstream-distribution-table-transport-group-text-color);
						"
					/>
					<Column
						header="Temperature Requirements"
						class="warehousing-group-subheader"
						style="
							background-color: var(--p-downstream-distribution-table-wharehousing-group-background-color);
							color: var(--p-downstream-distribution-table-wharehousing-group-text-color);
						"
					/>
					<Column
						header="Storage Time"
						class="warehousing-group-subheader"
						style="
							background-color: var(--p-downstream-distribution-table-wharehousing-group-background-color);
							color: var(--p-downstream-distribution-table-wharehousing-group-text-color);
						"
					/>
					<Column
						header="Unit"
						class="warehousing-group-subheader"
						style="
							background-color: var(--p-downstream-distribution-table-wharehousing-group-background-color);
							color: var(--p-downstream-distribution-table-wharehousing-group-text-color);
						"
					/>
					<Column
						header="Location"
						class="warehousing-group-subheader"
						style="
							background-color: var(--p-downstream-distribution-table-wharehousing-group-background-color);
							color: var(--p-downstream-distribution-table-wharehousing-group-text-color);
						"
					/>
					<Column
						header="Actions"
						:colspan="1"
						class="action-field-header"
						style="
							background-color: var(--p-downstream-distribution-table-action-field-background-color);
							color: var(--p-downstream-distribution-table-action-field-text-color);
						"
					/>
				</Row>
			</ColumnGroup>
			
			<Column field="stepName" header="Step">
				<template #body="{ data }">
					<div v-if="data.isTitle">
						<b>{{ data.stepName }}</b>
					</div>
					<div v-else-if="!data.isTitle && !data.lastLine">
						<!-- Empty cell for data rows -->
					</div>
				</template>
			</Column>
			
			<Column field="modeOfTransport" header="Mode of Transport">
				<template #body="{ data }">
					<div v-if="!data.isTitle && !data.lastLine">
						{{ data.modeOfTransport }}
					</div>
				</template>
			</Column>
			
			<Column field="distance" header="Distance">
				<template #body="{ data }">
					<div v-if="!data.isTitle && !data.lastLine">
						{{ data.distance }}
					</div>
				</template>
			</Column>
			
			<Column field="unit" header="Unit">
				<template #body="{ data }">
					<div v-if="!data.isTitle && !data.lastLine">
						{{ data.unit }}
					</div>
				</template>
			</Column>
			
			<Column field="tempRequirement" header="Temperature Requirements">
				<template #body="{ data }">
					<div v-if="!data.isTitle && !data.lastLine">
						{{ data.tempRequirement }}
					</div>
				</template>
			</Column>
			
			<Column field="storageTime" header="Storage Time">
				<template #body="{ data }">
					<div v-if="!data.isTitle && !data.lastLine">
						{{ data.storageTime }}
					</div>
				</template>
			</Column>
			
			<Column field="storageTimeUnit" header="Unit">
				<template #body="{ data }">
					<div v-if="!data.isTitle && !data.lastLine">
						{{ data.storageTimeUnit }}
					</div>
				</template>
			</Column>
			
			<Column field="location" header="Location">
				<template #body="{ data }">
					<div v-if="!data.isTitle && !data.lastLine">
						{{ data.location }}
					</div>
				</template>
			</Column>
			
			<Column field="actions" header="Actions">
				<template #body="{ data }">
					<div v-if="!data.isTitle && !data.lastLine" class="action-buttons">
						<i class="pi pi-pencil" title="Edit" style="cursor: pointer; margin-right: 8px;"></i>
						<i class="pi pi-trash" title="Delete" style="cursor: pointer;"></i>
					</div>
				</template>
			</Column>
		</DataTable>
	</div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'

const emit = defineEmits<{
	(e: "elementClick", event: MouseEvent): void
}>()

const handleClick = (event: MouseEvent) => {
	emit("elementClick", event)
}

// Example preview data
const previewData = ref([
	{
		stepName: "Step 1 - Manufacturing to Distribution Center",
		isTitle: true,
		lastLine: false
	},
	{
		stepName: "",
		modeOfTransport: "Road Transport",
		distance: "250",
		unit: "km",
		tempRequirement: "Ambient Temperature",
		storageTime: "7",
		storageTimeUnit: "days",
		location: "Regional Distribution Center",
		isTitle: false,
		lastLine: false
	},
	{
		stepName: "Step 2 - Distribution Center to Retailer",
		isTitle: true,
		lastLine: false
	},
	{
		stepName: "",
		modeOfTransport: "Road Transport",
		distance: "50",
		unit: "km",
		tempRequirement: "Refrigerated",
		storageTime: "2",
		storageTimeUnit: "days",
		location: "Retail Store",
		isTitle: false,
		lastLine: false
	}
])
</script>

<style scoped>
.downstream-distribution-preview {
	padding: 20px;
	background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
	margin: 16px 0;
}

.preview-header {
	margin-bottom: 20px;
	text-align: center;
}

.preview-header h3 {
	color: #495057;
	margin: 0 0 8px 0;
	font-size: 18px;
	font-weight: 600;
}

.preview-header p {
	color: #6c757d;
	margin: 0;
	font-size: 14px;
	font-style: italic;
}

.preview-table {
	width: 100%;
	border-collapse: collapse;
}

.preview-table :deep(.p-datatable-header) {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 8px 8px 0 0;
}

.preview-table :deep(.p-datatable-thead > tr > th) {
	border: 1px solid #dee2e6;
	padding: 12px 8px;
	font-weight: 600;
	font-size: 13px;
}

.preview-table :deep(.p-datatable-tbody > tr > td) {
	border: 1px solid #dee2e6;
	padding: 10px 8px;
	font-size: 13px;
	vertical-align: middle;
}

.preview-table :deep(.p-datatable-tbody > tr:hover) {
	background-color: rgba(0, 123, 255, 0.05);
}

.action-buttons {
	display: flex;
	gap: 8px;
	justify-content: center;
	align-items: center;
}

.action-buttons i {
	color: #6c757d;
	transition: color 0.3s ease;
}

.action-buttons i:hover {
	color: #007bff;
}

/* Responsive design */
@media (max-width: 1200px) {
	.preview-table :deep(.p-datatable-thead > tr > th),
	.preview-table :deep(.p-datatable-tbody > tr > td) {
		padding: 8px 6px;
		font-size: 12px;
	}
}

@media (max-width: 768px) {
	.downstream-distribution-preview {
		padding: 16px;
		margin: 12px 0;
	}
	
	.preview-header h3 {
		font-size: 16px;
	}
	
	.preview-header p {
		font-size: 13px;
	}
}
</style>